import {
    Page,
    Card,
    BlockStack,
    InlineStack,
    Text,
    Collapsible,
    Link,
    Button,
    ProgressBar,
    List,
    Toast,
} from '@shopify/polaris';
import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSettingAsync, getAllSetting, getSettingAsync } from '../redux/slice/commonSlice';
import { useAppFormik } from '../contexts/FormikContext';

// Define all step IDs
const STEP_IDS = {
    'use-strong-password': 'use-strong-password',
    'dont-share-password': 'dont-share-password',
    'phishing-attacks': 'phishing-attacks',
    'two-step-auth': 'two-step-auth',
    'using-passkeys': 'using-passkeys',
    'suspicious-logins': 'suspicious-logins',
    'staff-account': 'staff-account',
    'third-party-apps': 'third-party-apps',
};

const TOTAL_STEPS = 8;

const SecurityReviewPage = () => {
    const { isCreateSettingLoading } = useSelector(getAllSetting);
    const { values, setFieldValue } = useAppFormik();
    const dispatch = useDispatch();

    const [openSections, setOpenSections] = useState({
        'use-strong-password': true,
        'dont-share-password': true,
        'phishing-attacks': false,
        'two-step-auth': false,
        'using-passkeys': false,
        'suspicious-logins': false,
        'staff-account': false,
        'third-party-apps': false,
    });

    const [toastActive, setToastActive] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [completingStep, setCompletingStep] = useState(null);

    // Get completed steps from Formik
    const completedSteps = values.securityReview?.completedSteps || [];
    const completedStepsCount = completedSteps.length;
    const progress = (completedStepsCount / TOTAL_STEPS) * 100;

    const showToast = useCallback((message) => {
        setToastMessage(message);
        setToastActive(true);
    }, []);

    const toastMarkup = toastActive ? (
        <Toast content={toastMessage} onDismiss={() => setToastActive(false)} />
    ) : null;

    const toggleSection = useCallback((sectionId) => {
        setOpenSections((prev) => ({
            ...prev,
            [sectionId]: !prev[sectionId],
        }));
    }, []);

    const handleCompleteStep = async (stepId) => {
        if (completedSteps.includes(stepId)) {
            showToast('This step is already completed.');
            return;
        }

        setCompletingStep(stepId);

        // Add step to completed steps
        const updatedCompletedSteps = [...completedSteps, stepId];
        setFieldValue('securityReview.completedSteps', updatedCompletedSteps);

        // Prepare updated values
        const updatedValues = {
            ...values,
            securityReview: {
                ...values.securityReview,
                completedSteps: updatedCompletedSteps,
            },
        };

        // Call API to save
        try {
            await dispatch(
                createSettingAsync({
                    payload: { settings: updatedValues },
                    callback: () => {
                        dispatch(getSettingAsync({}));
                        showToast(`Step "${getStepTitle(stepId)}" completed successfully!`);
                        setCompletingStep(null);
                    },
                })
            );
        } catch (error) {
            showToast('Failed to complete step. Please try again.');
            setCompletingStep(null);
        }
    };

    const getStepTitle = (stepId) => {
        const titles = {
            'use-strong-password': 'Use Strong Password',
            'dont-share-password': "Don't Share Your Password",
            'phishing-attacks': 'Phishing and Other Attacks',
            'two-step-auth': 'Two Step Authentication',
            'using-passkeys': 'Using Passkeys',
            'suspicious-logins': 'Suspicious Logins',
            'staff-account': 'Staff Account Management',
            'third-party-apps': 'Third-Party Apps and Code',
        };
        return titles[stepId] || stepId;
    };

    const isStepCompleted = (stepId) => {
        return completedSteps.includes(stepId);
    };

    return (
        <Page
            title="Security Review"
            subtitle="Take a moment to review the security settings for your store."
        >
            <BlockStack gap="500">
                {/* Progress Bar */}
                <Card sectioned>
                    <BlockStack gap="300">
                        <ProgressBar progress={progress} size="small" />
                        <Text tone="subdued" variant="bodySm">
                            {completedStepsCount} of {TOTAL_STEPS} steps completed
                        </Text>
                    </BlockStack>
                </Card>

                {/* Use Strong Password Section */}
                <Card>
                    <BlockStack gap="400">
                        <InlineStack align="space-between" blockAlign="center">
                            <Text variant="headingMd" as="h2">
                                Use Strong Password
                            </Text>
                            <Button
                                plain
                                onClick={() => toggleSection('use-strong-password')}
                            >
                                {openSections['use-strong-password'] ? '−' : '+'}
                            </Button>
                        </InlineStack>
                        <Collapsible
                            open={openSections['use-strong-password']}
                            id="use-strong-password"
                            transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
                        >
                            <BlockStack gap="400">
                                <Text>
                                    As a Shopify store owner, protecting your store and customer data is crucial. One of the most important steps you can take is to use a strong password for your Shopify account.
                                </Text>

                                <BlockStack gap="300">
                                    <Text variant="headingSm" as="h3">
                                        Why Use a Strong Password?
                                    </Text>
                                    <Text>
                                        A strong password is your first line of defense against security breaches. Weak passwords are easy for hackers to guess, which can lead to serious consequences such as:
                                    </Text>
                                    <List type="bullet">
                                        <List.Item>Data theft</List.Item>
                                        <List.Item>Product price manipulation</List.Item>
                                        <List.Item>Website takedown</List.Item>
                                    </List>
                                    <Text>
                                        A strong password with a minimum of 12 characters makes it much harder for hackers to gain access to your account.
                                    </Text>
                                </BlockStack>

                                <BlockStack gap="300">
                                    <Text variant="headingSm" as="h3">
                                        Tips for Creating a Strong Password
                                    </Text>
                                    <List type="number">
                                        <List.Item>
                                            <Text fontWeight="semibold">Use a combination of characters:</Text> Include uppercase and lowercase letters, numbers, and special characters (e.g., @, #, $, %).
                                        </List.Item>
                                        <List.Item>
                                            <Text fontWeight="semibold">Avoid personal information:</Text> Do not use personal details like name, birth date, or address.
                                        </List.Item>
                                        <List.Item>
                                            <Text fontWeight="semibold">Use a passphrase:</Text> Combine words for a long and complex password (e.g., "mydoglikeslongwalks" is better than "password123").
                                        </List.Item>
                                        <List.Item>
                                            <Text fontWeight="semibold">Use a password manager:</Text> These tools help create and store complex, random passwords. Recommended password managers: <Link url="https://1password.com" external>1Password</Link> and <Link url="https://bitwarden.com" external>Bitwarden</Link>.
                                        </List.Item>
                                        <List.Item>
                                            <Text fontWeight="semibold">Change your password regularly:</Text> It's recommended to change your password every 3 months, especially if you suspect it may have been compromised.
                                        </List.Item>
                                    </List>
                                </BlockStack>

                                <InlineStack align="end">
                                    <Button
                                        variant="primary"
                                        tone="success"
                                        onClick={() => handleCompleteStep(STEP_IDS['use-strong-password'])}
                                        loading={completingStep === STEP_IDS['use-strong-password'] && isCreateSettingLoading}
                                        disabled={isStepCompleted(STEP_IDS['use-strong-password']) || isCreateSettingLoading}
                                    >
                                        {isStepCompleted(STEP_IDS['use-strong-password']) ? 'Completed' : 'Complete Step'}
                                    </Button>
                                </InlineStack>
                            </BlockStack>
                        </Collapsible>
                    </BlockStack>
                </Card>

                {/* Don't Share Your Password Section */}
                <Card>
                    <BlockStack gap="400">
                        <InlineStack align="space-between" blockAlign="center">
                            <Text variant="headingMd" as="h2">
                                Don't Share Your Password
                            </Text>
                            <Button
                                plain
                                onClick={() => toggleSection('dont-share-password')}
                            >
                                {openSections['dont-share-password'] ? '−' : '+'}
                            </Button>
                        </InlineStack>
                        <Collapsible
                            open={openSections['dont-share-password']}
                            id="dont-share-password"
                            transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
                        >
                            <BlockStack gap="400">
                                <Text>
                                    Never use the same password on multiple websites. Use a password manager to create unique passwords for each site you use.
                                </Text>
                                <Text>
                                    While Shopify has a security team that works around the clock to keep your store safe, this isn't true for all other online services you use.
                                </Text>
                                <Text>
                                    Using the same password across multiple sites increases your risk of exposure. If your login credentials are leaked from one site, they can end up on the dark web and be used to access other services you use.
                                </Text>
                                <Text>
                                    Never share your password with anyone, even if they say it's "just this one time." Passwords are easily stored in browsers, and once shared, you can't control who has access to your account. The only way to fix this is to change your password and make sure the other person is logged out.
                                </Text>
                                <Text>
                                    Instead of sharing your password, you can easily <Link url="https://help.shopify.com/en/manual/your-account/staff-accounts" external>create a staff account</Link> in Shopify or use the <Link url="https://help.shopify.com/en/manual/your-account/collaborator-access" external>collaborator access tool</Link> for sharing access with third-party companies.
                                </Text>
                                <InlineStack align="end">
                                    <Button
                                        variant="primary"
                                        tone="success"
                                        onClick={() => handleCompleteStep(STEP_IDS['dont-share-password'])}
                                        loading={completingStep === STEP_IDS['dont-share-password'] && isCreateSettingLoading}
                                        disabled={isStepCompleted(STEP_IDS['dont-share-password']) || isCreateSettingLoading}
                                    >
                                        {isStepCompleted(STEP_IDS['dont-share-password']) ? 'Completed' : 'Complete Step'}
                                    </Button>
                                </InlineStack>
                            </BlockStack>
                        </Collapsible>
                    </BlockStack>
                </Card>

                {/* Phishing and Other Attacks Section */}
                <Card>
                    <BlockStack gap="400">
                        <InlineStack align="space-between" blockAlign="center">
                            <Text variant="headingMd" as="h2">
                                Phishing and Other Attacks
                            </Text>
                            <Button
                                plain
                                onClick={() => toggleSection('phishing-attacks')}
                            >
                                {openSections['phishing-attacks'] ? '−' : '+'}
                            </Button>
                        </InlineStack>
                        <Collapsible
                            open={openSections['phishing-attacks']}
                            id="phishing-attacks"
                            transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
                        >
                            <BlockStack gap="300">
                                <Text>
                                    Learn how to identify and protect yourself from phishing attacks and other security threats that could compromise your Shopify store.
                                </Text>
                                <Text>
                                    Phishing attacks are attempts to trick you into revealing sensitive information like passwords or credit card numbers by pretending to be a legitimate organization.
                                </Text>
                                <InlineStack align="end">
                                    <Button
                                        variant="primary"
                                        tone="success"
                                        onClick={() => handleCompleteStep(STEP_IDS['phishing-attacks'])}
                                        loading={completingStep === STEP_IDS['phishing-attacks'] && isCreateSettingLoading}
                                        disabled={isStepCompleted(STEP_IDS['phishing-attacks']) || isCreateSettingLoading}
                                    >
                                        {isStepCompleted(STEP_IDS['phishing-attacks']) ? 'Completed' : 'Complete Step'}
                                    </Button>
                                </InlineStack>
                            </BlockStack>
                        </Collapsible>
                    </BlockStack>
                </Card>

                {/* Two Step Authentication Section */}
                <Card>
                    <BlockStack gap="400">
                        <InlineStack align="space-between" blockAlign="center">
                            <Text variant="headingMd" as="h2">
                                Two Step Authentication
                            </Text>
                            <Button
                                plain
                                onClick={() => toggleSection('two-step-auth')}
                            >
                                {openSections['two-step-auth'] ? '−' : '+'}
                            </Button>
                        </InlineStack>
                        <Collapsible
                            open={openSections['two-step-auth']}
                            id="two-step-auth"
                            transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
                        >
                            <BlockStack gap="300">
                                <Text>
                                    Enable two-step authentication to add an extra layer of security to your Shopify account. This requires a second verification step in addition to your password.
                                </Text>
                                <InlineStack align="end">
                                    <Button
                                        variant="primary"
                                        tone="success"
                                        onClick={() => handleCompleteStep(STEP_IDS['two-step-auth'])}
                                        loading={completingStep === STEP_IDS['two-step-auth'] && isCreateSettingLoading}
                                        disabled={isStepCompleted(STEP_IDS['two-step-auth']) || isCreateSettingLoading}
                                    >
                                        {isStepCompleted(STEP_IDS['two-step-auth']) ? 'Completed' : 'Complete Step'}
                                    </Button>
                                </InlineStack>
                            </BlockStack>
                        </Collapsible>
                    </BlockStack>
                </Card>

                {/* Using Passkeys Section */}
                <Card>
                    <BlockStack gap="400">
                        <InlineStack align="space-between" blockAlign="center">
                            <Text variant="headingMd" as="h2">
                                Using Passkeys
                            </Text>
                            <Button
                                plain
                                onClick={() => toggleSection('using-passkeys')}
                            >
                                {openSections['using-passkeys'] ? '−' : '+'}
                            </Button>
                        </InlineStack>
                        <Collapsible
                            open={openSections['using-passkeys']}
                            id="using-passkeys"
                            transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
                        >
                            <BlockStack gap="300">
                                <Text>
                                    Passkeys provide a more secure and convenient way to sign in to your Shopify account using biometric authentication or device PIN.
                                </Text>
                                <InlineStack align="end">
                                    <Button
                                        variant="primary"
                                        tone="success"
                                        onClick={() => handleCompleteStep(STEP_IDS['using-passkeys'])}
                                        loading={completingStep === STEP_IDS['using-passkeys'] && isCreateSettingLoading}
                                        disabled={isStepCompleted(STEP_IDS['using-passkeys']) || isCreateSettingLoading}
                                    >
                                        {isStepCompleted(STEP_IDS['using-passkeys']) ? 'Completed' : 'Complete Step'}
                                    </Button>
                                </InlineStack>
                            </BlockStack>
                        </Collapsible>
                    </BlockStack>
                </Card>

                {/* Suspicious Logins Section */}
                <Card>
                    <BlockStack gap="400">
                        <InlineStack align="space-between" blockAlign="center">
                            <Text variant="headingMd" as="h2">
                                Suspicious Logins
                            </Text>
                            <Button
                                plain
                                onClick={() => toggleSection('suspicious-logins')}
                            >
                                {openSections['suspicious-logins'] ? '−' : '+'}
                            </Button>
                        </InlineStack>
                        <Collapsible
                            open={openSections['suspicious-logins']}
                            id="suspicious-logins"
                            transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
                        >
                            <BlockStack gap="300">
                                <Text>
                                    Monitor and review suspicious login attempts to your Shopify account. Learn how to identify and respond to unauthorized access attempts.
                                </Text>
                                <InlineStack align="end">
                                    <Button
                                        variant="primary"
                                        tone="success"
                                        onClick={() => handleCompleteStep(STEP_IDS['suspicious-logins'])}
                                        loading={completingStep === STEP_IDS['suspicious-logins'] && isCreateSettingLoading}
                                        disabled={isStepCompleted(STEP_IDS['suspicious-logins']) || isCreateSettingLoading}
                                    >
                                        {isStepCompleted(STEP_IDS['suspicious-logins']) ? 'Completed' : 'Complete Step'}
                                    </Button>
                                </InlineStack>
                            </BlockStack>
                        </Collapsible>
                    </BlockStack>
                </Card>

                {/* Staff Account Management Section */}
                <Card>
                    <BlockStack gap="400">
                        <InlineStack align="space-between" blockAlign="center">
                            <Text variant="headingMd" as="h2">
                                Staff Account Management
                            </Text>
                            <Button
                                plain
                                onClick={() => toggleSection('staff-account')}
                            >
                                {openSections['staff-account'] ? '−' : '+'}
                            </Button>
                        </InlineStack>
                        <Collapsible
                            open={openSections['staff-account']}
                            id="staff-account"
                            transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
                        >
                            <BlockStack gap="300">
                                <Text>
                                    Manage staff accounts securely. Learn how to create, modify, and remove staff accounts while maintaining proper access controls.
                                </Text>
                                <InlineStack align="end">
                                    <Button
                                        variant="primary"
                                        tone="success"
                                        onClick={() => handleCompleteStep(STEP_IDS['staff-account'])}
                                        loading={completingStep === STEP_IDS['staff-account'] && isCreateSettingLoading}
                                        disabled={isStepCompleted(STEP_IDS['staff-account']) || isCreateSettingLoading}
                                    >
                                        {isStepCompleted(STEP_IDS['staff-account']) ? 'Completed' : 'Complete Step'}
                                    </Button>
                                </InlineStack>
                            </BlockStack>
                        </Collapsible>
                    </BlockStack>
                </Card>

                {/* Third-Party Apps and Code Section */}
                <Card>
                    <BlockStack gap="400">
                        <InlineStack align="space-between" blockAlign="center">
                            <Text variant="headingMd" as="h2">
                                Third-Party Apps and Code
                            </Text>
                            <Button
                                plain
                                onClick={() => toggleSection('third-party-apps')}
                            >
                                {openSections['third-party-apps'] ? '−' : '+'}
                            </Button>
                        </InlineStack>
                        <Collapsible
                            open={openSections['third-party-apps']}
                            id="third-party-apps"
                            transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
                        >
                            <BlockStack gap="300">
                                <Text>
                                    Understand the security implications of installing third-party apps and custom code. Learn how to evaluate and manage app permissions safely.
                                </Text>
                                <InlineStack align="end">
                                    <Button
                                        variant="primary"
                                        tone="success"
                                        onClick={() => handleCompleteStep(STEP_IDS['third-party-apps'])}
                                        loading={completingStep === STEP_IDS['third-party-apps'] && isCreateSettingLoading}
                                        disabled={isStepCompleted(STEP_IDS['third-party-apps']) || isCreateSettingLoading}
                                    >
                                        {isStepCompleted(STEP_IDS['third-party-apps']) ? 'Completed' : 'Complete Step'}
                                    </Button>
                                </InlineStack>
                            </BlockStack>
                        </Collapsible>
                    </BlockStack>
                </Card>
            </BlockStack>
            {toastMarkup}
        </Page>
    );
};

export default SecurityReviewPage;
